import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { Box, Button, Tooltip, useTheme } from '@mui/material';

import { AddressNoteApi } from '@/api';
import { AppModal } from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import { IAddressNoteForm } from '@/models';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';

import AddressNoteDataGrid from './components/AddressNoteDataGrid';
import { AddressNoteRoot, cautionPulse, cautionGrow } from './styles';

interface AddressNoteModalProps extends WithLoaderProps {
  addressId?: string;
}

type AddressNoteForm = IAddressNoteForm;

const AddressNoteModal: FC<AddressNoteModalProps> = ({ addressId }) => {
  const { updateToast } = useToastStore();
  const theme = useTheme();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [cautionCount, setCautionCount] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasBeenClicked, setHasBeenClicked] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const methods = useForm<AddressNoteForm>({
    mode: 'all',
  });
  const { reset } = methods;

  useEffect(() => {
    setHasBeenClicked(false);
  }, [addressId]);

  useEffect(() => {
    if (addressId) {
      fetchAddressNotes();
    }
  }, [addressId, refreshTrigger]);

  const fetchAddressNotes = async () => {
    try {
      const params = {
        sort: JSON.stringify({ createdAt: -1 }),
        filter: JSON.stringify({ address: addressId }),
      };
      const res = await AddressNoteApi.getAddressNotes(params);

      setTotalCount(res.data.results.length);
      // Count notes with isCaution = true
      const cautionNotes = res.data.results.filter(
        (note) => note.type.isCaution,
      );
      setCautionCount(cautionNotes.length);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onClose = () => {
    setOpenModal(false);
    // Trigger a refresh of the caution count when modal closes
    setRefreshTrigger((prev) => prev + 1);
    reset();
  };

  const hasCaution = cautionCount > 0;
  const shouldPulse = hasCaution && !hasBeenClicked;

  const handleOpen = () => {
    setHasBeenClicked(true);
    setOpenModal(true);
  };

  return (
    <AddressNoteRoot>
      <Tooltip title="Address Notes">
        <Button
          color="primary"
          onClick={handleOpen}
          sx={{
            height: '36px',
            minWidth: '36px',
            padding: 0,
            borderRadius: '8px',
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : colors.grey[100],
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? '#223640' : '#F5F7F7',
            },
          }}
          aria-label="Address Notes"
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                '&::before': shouldPulse
                  ? {
                      content: '""',
                      position: 'absolute',
                      inset: '-6px',
                      borderRadius: '6px',
                      backgroundColor: 'error.dark',
                      opacity: 0.55,
                      transformOrigin: 'center',
                      animation: `${cautionPulse} 1s linear infinite`,
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: hasCaution
                    ? colors.error.main
                    : theme.palette.mode === 'dark'
                    ? theme.palette.background.formSubHeader
                    : colors.grey[20],
                  color: hasCaution
                    ? colors.white
                    : theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : colors.grey[100],
                  borderRadius: '6px',
                  padding: '0 6px',
                  width: '44px',
                  minWidth: '44px',
                  height: '24px',
                  boxSizing: 'border-box',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  gap: '3px',
                  transformOrigin: 'center',
                  ...(shouldPulse && {
                    animation: `${cautionGrow} 1s linear infinite`,
                  }),
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ArticleOutlinedIcon
                    sx={{ fontSize: 20, color: 'inherit' }}
                  />
                </Box>
                {totalCount > 0 && (
                  <Box component="span" sx={{ fontSize: '0.7rem' }}>
                    {totalCount}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Button>
      </Tooltip>
      <AppModal open={openModal} title="Address Notes" onClose={onClose}>
        <AddressNoteDataGrid
          addressId={addressId}
          onNoteChange={() => setRefreshTrigger((prev) => prev + 1)}
        />
      </AppModal>
    </AddressNoteRoot>
  );
};

export default withLoader(AddressNoteModal);
